import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { ClientFile } from '@/types/coach';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set: edit existing. When null/undefined: create new. */
  initial?: ClientFile;
  onSaved: (saved: ClientFile) => void;
}

export function ClientForm({ open, onOpenChange, initial, onSaved }: Props) {
  const isEdit = !!initial;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [goals, setGoals] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirstName(initial?.firstName ?? '');
    setLastName(initial?.lastName ?? '');
    setEmail(initial?.email ?? '');
    setPhone(initial?.phone ?? '');
    setNotes(initial?.notes ?? '');
    setGoals(initial?.goals ?? '');
  }, [open, initial]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Vorname und Nachname sind Pflicht.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        notes: notes.trim() || null,
        goals: goals.trim() || null,
      };
      const saved = isEdit && initial
        ? await api.coach.updateClient(initial.id, payload)
        : await api.coach.createClient(payload);
      toast.success(isEdit ? 'Änderungen gespeichert.' : 'Klient angelegt.');
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Speichern fehlgeschlagen.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Klient bearbeiten' : 'Neuen Klient anlegen'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-sm font-medium">Vorname *</span>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="mt-1"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Nachname *</span>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="mt-1"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium">E-Mail</span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Telefon</span>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Allgemeine Notizen</span>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 text-sm resize-none"
              placeholder="z.B. Hintergrund, Empfohlen über..."
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Ziel</span>
            <Textarea
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              rows={3}
              className="mt-1 text-sm resize-none"
              placeholder="Was möchte der Klient erreichen?"
            />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Speichere…' : isEdit ? 'Speichern' : 'Anlegen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
